import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import './CRM.css';

const API = '/api/crm';
const token = () => localStorage.getItem('intranet_token');
const auth  = () => ({ headers: { Authorization: `Bearer ${token()}` } });

const initials = name =>
  (name || '?').trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();

// Profile photo source: use an inline data URI when present (single-contact
// responses still carry it), otherwise load lazily from the photo endpoint so
// list payloads stay free of base64 blobs. Returns null when there's no photo.
const photoSrc = (c) =>
  c?.photo_url || (c?.has_photo ? `${API}/contacts/${c.id}/photo?token=${token()}` : null);

// Marks a value that was auto-filled by Apollo enrichment (unverified).
const ApolloMark = () => (
  <span className="crm-apollo-mark" title="Auto-filled by Apollo — verify accuracy">✦</span>
);

const fmtEmpDate = (d) => {
  if (!d) return '';
  const [y, m] = d.split('-');
  if (!m || m === '01') return y; // year-only dates from Apollo are stored as YYYY-01-01
  const month = new Date(parseInt(y), parseInt(m) - 1).toLocaleString('default', { month: 'short' });
  return `${month} ${y}`;
};

// hintDomain: if already known from email inference, skip Clearbit autocomplete entirely
const fetchCompanyInfo = async (name, hintDomain = null) => {
  if (!name?.trim()) return null;
  try {
    let domain = hintDomain;

    if (!domain) {
      // Clearbit autocomplete → domain only (ignore its name, which can be wrong for short tickers)
      const cbRes = await axios.get(
        `https://autocomplete.clearbit.com/v1/companies/suggest?query=${encodeURIComponent(name.trim())}`
      );
      const suggestions = cbRes.data || [];
      const q = name.trim().toLowerCase();
      const match = suggestions.find(c => c.name.toLowerCase() === q) || suggestions[0] || null;
      domain = match?.domain || null;
    }

    const result = { name: name.trim(), domain, description: null };

    // Wikipedia: always search by the original name, not Clearbit's resolved name
    const wikiTarget = name.trim();
    try {
      const getSummary = async (title) => {
        const r = await axios.get(
          `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`
        );
        return r.data?.type !== 'disambiguation' && r.data?.extract ? r.data.extract : null;
      };

      let extract = await getSummary(wikiTarget);

      if (!extract) {
        const searchRes = await axios.get('https://en.wikipedia.org/w/api.php', {
          params: { action: 'opensearch', search: `${wikiTarget} company`, limit: 3, format: 'json', origin: '*' },
        });
        const titles = searchRes.data?.[1] || [];
        for (const t of titles) {
          extract = await getSummary(t);
          if (extract) break;
        }
      }

      if (extract) {
        result.description = extract.length > 300 ? extract.slice(0, 300).trimEnd() + '…' : extract;
      }
    } catch { /* Wikipedia unavailable */ }

    return result;
  } catch {
    return null;
  }
};

/* Logo: Google favicon (reliable, sourced from actual website) */
const CompanyLogo = ({ domain, className }) => {
  const [gone, setGone] = useState(false);
  useEffect(() => { setGone(false); }, [domain]);
  if (!domain || gone) return null;
  return (
    <img
      className={className}
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`}
      alt=""
      onError={() => setGone(true)}
    />
  );
};

const FIELDS = [
  { key: 'first_name',       label: 'First Name' },
  { key: 'last_name',        label: 'Last Name' },
  { key: 'name',             label: 'Full Name (combined)' },
  { key: 'work_email',       label: 'Work Email' },
  { key: 'personal_email',   label: 'Personal Email' },
  { key: 'company',          label: 'Company' },
  { key: 'position',         label: 'Position' },
  { key: 'work_phone',       label: 'Work Phone' },
  { key: 'personal_phone',   label: 'Personal Phone' },
  { key: 'work_address',     label: 'Work Address' },
  { key: 'personal_address', label: 'Personal Address' },
  { key: 'attorney',         label: 'Assigned Attorney' },
];

/* ─── Position hierarchy for company drill-down ──────────────────────────── */
const POSITION_RANK = [
  { rank: 1,  terms: ['managing partner', 'managing director', 'president'] },
  { rank: 2,  terms: ['ceo', 'chief executive'] },
  { rank: 3,  terms: ['gc', 'general counsel', 'clo', 'chief legal'] },
  { rank: 4,  terms: ['co gc', 'co-gc', 'deputy gc', 'co-founder', 'founder'] },
  { rank: 5,  terms: ['evp', 'executive vice president'] },
  { rank: 6,  terms: ['svp', 'senior vice president'] },
  { rank: 7,  terms: ['cfo', 'chief financial', 'coo', 'chief operating'] },
  { rank: 8,  terms: ['vp', 'vice president'] },
  { rank: 9,  terms: ['partner'] },
  { rank: 10, terms: ['associate gc', 'assistant gc', 'associate general counsel'] },
  { rank: 11, terms: ['senior counsel', 'senior partner', 'senior attorney'] },
  { rank: 12, terms: ['director', 'head of'] },
  { rank: 13, terms: ['counsel', 'attorney', 'lawyer', 'judge'] },
  { rank: 14, terms: ['associate counsel', 'associate'] },
  { rank: 15, terms: ['manager', 'claim'] },
];

function posRank(pos) {
  if (!pos) return 99;
  const p = pos.toLowerCase();
  for (const { rank, terms } of POSITION_RANK) {
    if (terms.some(t => p.includes(t))) return rank;
  }
  return 50;
}

/* ─── Sortable column header ──────────────────────────────────────────────── */
const SortTh = ({ col, label, sortCol, sortDir, onSort, className = '' }) => {
  const active = sortCol === col;
  return (
    <th
      className={`crm-th-sort ${active ? 'crm-th-sort--on' : ''} ${className}`}
      onClick={() => onSort(col)}
    >
      {label}
      <span className="crm-sort-arrow">{active ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}</span>
    </th>
  );
};

/* ─── Shared company view (used in Company tab + CompanyModal) ────────────── */
const CompanyView = ({ companyName, contacts, compInfo, activeContactId, onOpenContact }) => {
  const sorted = useMemo(() =>
    [...contacts].sort((a, b) => posRank(a.position) - posRank(b.position) || (a.name || '').localeCompare(b.name || '')),
  [contacts]);

  return (
    <>
      <div className="crm-co-card">
        {compInfo?.domain && <CompanyLogo domain={compInfo.domain} className="crm-co-card-logo" />}
        <div className="crm-co-card-body">
          <span className="crm-co-card-name">{compInfo?.name || companyName}</span>
          {compInfo?.domain && (
            <a className="crm-co-card-domain" href={`https://${compInfo.domain}`} target="_blank" rel="noopener noreferrer">
              {compInfo.domain} ↗
            </a>
          )}
          {compInfo?.description && <p className="crm-co-card-desc">{compInfo.description}</p>}
        </div>
      </div>

      <p className="crm-detail-section-hd crm-co-connections-hd">
        Known Connections at {compInfo?.name || companyName} ({sorted.length})
      </p>

      {sorted.length === 0 ? (
        <p className="crm-co-solo">No contacts on record at this company.</p>
      ) : (
        <div className="crm-co-list">
          {sorted.map((c, i) => {
            const isActive = c.id === activeContactId;
            return (
              <div
                key={c.id}
                className={`crm-co-row${isActive ? ' crm-co-row--active' : ''}${onOpenContact && !isActive ? ' crm-co-row--link' : ''}`}
                onClick={onOpenContact && !isActive ? () => onOpenContact(c) : undefined}
              >
                <span className="crm-co-rank">{i + 1}</span>
                <div className="crm-co-info">
                  <span className="crm-co-name">
                    {c.name}
                    {isActive && <span className="crm-co-you"> · viewing</span>}
                  </span>
                  {c.position && <span className="crm-co-pos">{c.position}</span>}
                </div>
                {c.work_email && (
                  <a className="crm-co-email" href={`mailto:${c.work_email}`} onClick={e => e.stopPropagation()}>
                    {c.work_email}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

/* ─── Company drill-down modal (from main table click) ───────────────────── */
const CompanyModal = ({ companyName, onClose, onOpenContact }) => {
  const [people,   setPeople]   = useState(null);
  const [compInfo, setCompInfo] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    axios.get(`${API}/contacts`, { ...auth(), params: { company: companyName } })
      .then(r => setPeople(r.data.filter(c => (c.company || '').toLowerCase() === companyName.toLowerCase())))
      .catch(() => setPeople([]));
    fetchCompanyInfo(companyName).then(info => { if (info) setCompInfo(info); });
  }, [companyName]);

  useEffect(() => {
    const onKey   = e => { if (e.key === 'Escape') onClose(); };
    const onClick = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick); };
  }, [onClose]);

  return (
    <div className="crm-overlay">
      <div className="crm-company-modal" ref={ref}>
        <button className="crm-detail-x" onClick={onClose}>✕</button>
        <div className="crm-company-modal-body">
          {people === null ? (
            <p className="crm-em" style={{ padding: '40px', textAlign: 'center' }}>Loading…</p>
          ) : (
            <CompanyView
              companyName={companyName}
              contacts={people}
              compInfo={compInfo}
              activeContactId={null}
              onOpenContact={c => { onClose(); onOpenContact(c); }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Activity type → accent color ──────────────────────────────────────── */
const actTypeColor = (type) => {
  if (!type) return '#6366f1';
  const t = type.toLowerCase();
  if (t.includes('coffee') || t.includes('lunch') || t.includes('dinner') || t.includes('meal') || t.includes('drinks')) return '#f59e0b';
  if (t.includes('call') || t.includes('phone') || t.includes('zoom') || t.includes('video')) return '#10b981';
  if (t.includes('email') || t.includes('message') || t.includes('text')) return '#3b82f6';
  if (t.includes('event') || t.includes('conference') || t.includes('gala') || t.includes('reception') || t.includes('club')) return '#ec4899';
  if (t.includes('refer') || t.includes('intro')) return '#06b6d4';
  if (t.includes('meet') || t.includes('visit') || t.includes('breakfast')) return '#8b5cf6';
  return '#6366f1';
};

/* ─── Relationship type options (shared) ─────────────────────────────────── */
const REL_OPTIONS = [
  'Client', 'Prospect', 'Referral Source', 'Co-Counsel', 'Opposing Counsel',
  'Expert Witness', 'Judge / Court', 'Government', 'Friend / Personal', 'Other',
];

/* ─── Contact detail modal (Attio-style two-panel) ───────────────────────── */
const CI_ICON = {
  email: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/>
    </svg>
  ),
  phone: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.45 2.33.69 3.58.69a1 1 0 011 1V20a1 1 0 01-1 1C9.61 21 3 14.39 3 6a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.24 2.46.69 3.58a1 1 0 01-.24 1.01L6.62 10.79z"/>
    </svg>
  ),
  address: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/>
    </svg>
  ),
  linkedin: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
  industry: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
  ),
};

const CONTACT_FIELDS = [
  { label: 'Work Email',    field: 'work_email',    type: 'email', href: v => `mailto:${v}`,                                    icon: CI_ICON.email,    iconColor: '#2563eb', iconBg: '#eff6ff' },
  { label: 'Personal Email',field: 'personal_email',type: 'email', href: v => `mailto:${v}`,                                    icon: CI_ICON.email,    iconColor: '#7c3aed', iconBg: '#f5f3ff' },
  { label: 'Work Phone',    field: 'work_phone',    type: 'tel',   href: v => `tel:${v}`,                                       icon: CI_ICON.phone,    iconColor: '#16a34a', iconBg: '#f0fdf4' },
  { label: 'Personal Phone',field: 'personal_phone',type: 'tel',   href: v => `tel:${v}`,                                       icon: CI_ICON.phone,    iconColor: '#0891b2', iconBg: '#ecfeff' },
  { label: 'Work Address',  field: 'work_address',  type: 'text',                                                                icon: CI_ICON.address,  iconColor: '#ea580c', iconBg: '#fff7ed' },
  { label: 'Industry',      field: 'industry',      type: 'text',                                                                icon: CI_ICON.industry, iconColor: '#0d9488', iconBg: '#f0fdfa' },
  { label: 'LinkedIn',      field: 'linkedin_url',  type: 'url',   href: v => v, external: true, editOnly: true,        icon: CI_ICON.linkedin, iconColor: '#0a66c2', iconBg: '#e8f0fb' },
];

const ContactModal = ({ contact, isAdmin, isMythical, onClose, onUpdate, onDelete }) => {
  const [editing, setEditing]     = useState(false);
  const [form, setForm]           = useState({});
  const [attorneys, setAttorneys] = useState([]);
  const [saving, setSaving]       = useState(false);
  const [lists, setLists]         = useState([]); // marketing lists this contact is in
  const [tab, setTab]             = useState('overview');
  const [coContacts, setCoContacts] = useState(null); // null = not yet fetched
  const [compInfo,   setCompInfo]   = useState(null);
  const [activities, setActivities] = useState(null); // null = not yet fetched
  const [actForm, setActForm]       = useState({ activity_date: '', activity_type: '', note: '', logged_by_id: '' });
  const [addingAct, setAddingAct]   = useState(false);
  const [savingAct, setSavingAct]   = useState(false);
  const [enriching, setEnriching]   = useState(false);
  const [enrichMsg, setEnrichMsg]   = useState('');
  const [employment, setEmployment] = useState(null);
  const [menuOpen, setMenuOpen]     = useState(false);
  const [connections, setConnections] = useState(contact.connections || []);
  const [addingConn, setAddingConn] = useState(false);
  const [connAtty, setConnAtty]     = useState('');
  const [connRel, setConnRel]       = useState('');
  const ref     = useRef(null);
  const menuRef = useRef(null);

  const addConnection = async () => {
    if (!connAtty) return;
    try {
      const { data } = await axios.post(
        `${API}/contacts/${contact.id}/connections`,
        { attorney_id: connAtty, relationship_type: connRel || null },
        auth()
      );
      const next = [...connections.filter(c => String(c.attorney_id) !== String(data.attorney_id)), data];
      setConnections(next);
      setConnAtty(''); setConnRel(''); setAddingConn(false);
      onUpdate({ ...contact, connections: next });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add connection');
    }
  };

  const removeConnection = async (attorneyId) => {
    await axios.delete(`${API}/contacts/${contact.id}/connections/${attorneyId}`, auth()).catch(() => {});
    const next = connections.filter(c => String(c.attorney_id) !== String(attorneyId));
    setConnections(next);
    onUpdate({ ...contact, connections: next });
  };

  const switchTab = (t) => {
    setTab(t);
    if (t === 'company' && contact.company) {
      if (coContacts === null) {
        axios.get(`${API}/contacts`, { ...auth(), params: { company: contact.company } })
          .then(r => setCoContacts(
            r.data.filter(c => c.company?.toLowerCase() === contact.company?.toLowerCase())
          ))
          .catch(() => setCoContacts([]));
      }
      if (!compInfo) {
        fetchCompanyInfo(contact.company).then(info => { if (info) setCompInfo(info); });
      }
    }
  };

  const submitActivity = async () => {
    if (!actForm.activity_type.trim() || savingAct) return;
    setSavingAct(true);
    try {
      const payload = { ...actForm, logged_by_staff_id: actForm.logged_by_id || undefined };
      const { data } = await axios.post(`${API}/contacts/${contact.id}/activities`, payload, auth());
      setActivities(prev => [...(prev || []), data]);
      setActForm({ activity_date: '', activity_type: '', note: '', logged_by_id: '' });
      setAddingAct(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add activity');
    } finally { setSavingAct(false); }
  };

  const deleteActivity = async (actId) => {
    await axios.delete(`${API}/contacts/${contact.id}/activities/${actId}`, auth()).catch(() => {});
    setActivities(prev => prev.filter(a => a.id !== actId));
  };

  useEffect(() => {
    setForm({
      name: contact.name || '', work_email: contact.work_email || '',
      personal_email: contact.personal_email || '', company: contact.company || '',
      position: contact.position || '', work_phone: contact.work_phone || '',
      personal_phone: contact.personal_phone || '', work_address: contact.work_address || '',
      personal_address: contact.personal_address || '', attorney_id: contact.attorney_id || '',
      relationship_type: contact.relationship_type || '', notes: contact.notes || '',
      linkedin_url: contact.linkedin_url || '', industry: contact.industry || '',
    });
    axios.get(`${API}/contacts/${contact.id}/lists`, auth()).then(r => setLists(r.data || [])).catch(() => setLists([]));
    setEditing(false);
    setConnections(contact.connections || []); setAddingConn(false); setConnAtty(''); setConnRel('');
    setCoContacts(null); setCompInfo(null); setActivities(null); setEmployment(null);
    setAddingAct(false); setActForm({ activity_date: '', activity_type: '', note: '', logged_by_id: '' });
    setEnrichMsg(''); setTab('overview');
  }, [contact]);

  useEffect(() => {
    axios.get(`${API}/staff`, auth()).then(r => setAttorneys(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (employment !== null) return;
    axios.get(`${API}/contacts/${contact.id}/employment`, auth())
      .then(r => setEmployment(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEmployment([]));
  }, [contact.id, employment]);

  // Eagerly fetch activities so score is accurate on open
  useEffect(() => {
    axios.get(`${API}/contacts/${contact.id}/activities`, auth())
      .then(r => setActivities(Array.isArray(r.data) ? r.data : []))
      .catch(() => setActivities([]));
  }, [contact.id]);

  useEffect(() => {
    const onKey   = e => { if (e.key === 'Escape') { setMenuOpen(false); onClose(); } };
    const onClick = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick); };
  }, [onClose]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/contacts/${contact.id}`, form, auth());
      onUpdate(res.data); setEditing(false);
    } catch (err) {
      alert('Save failed: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${contact.name}? This cannot be undone.`)) return;
    await axios.delete(`${API}/contacts/${contact.id}`, auth());
    onDelete(contact.id); onClose();
  };

  const enrich = async () => {
    if (enriching) return;
    setEnriching(true); setEnrichMsg('');
    try {
      const { data } = await axios.post(`${API}/contacts/${contact.id}/enrich`, {}, auth());
      onUpdate(data.contact);
      setEmployment(null); // force reload of employment on next view
      const parts = [];
      if (data.enriched > 0) parts.push(`${data.enriched} field${data.enriched === 1 ? '' : 's'}`);
      if (data.jobs > 0) parts.push(`${data.jobs} job${data.jobs === 1 ? '' : 's'}`);
      setEnrichMsg(parts.length ? `✓ Enriched (${parts.join(', ')})` : '✓ Already up to date');
    } catch (err) {
      setEnrichMsg('✗ ' + (err.response?.data?.error || 'Enrichment failed'));
    } finally { setEnriching(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isEnriched = (f) => (contact.enriched_fields || []).includes(f);

  return (
    <div className="crm-overlay">
      <div className="crm-detail-modal" ref={ref}>
        <button className="crm-detail-x" onClick={onClose}>✕</button>

        {/* ── Left sidebar ─────────────────────────────────────────────── */}
        <div className="crm-detail-left">
          {/* Three-dot actions menu */}
          {!editing && (
            <div className="crm-dots-wrap" ref={menuRef}>
              <button className="crm-dots-btn" onClick={() => setMenuOpen(o => !o)}>•••</button>
              {menuOpen && (
                <div className="crm-dots-menu">
                  <button className="crm-dots-item" onClick={() => { setEditing(true); setMenuOpen(false); }}>Edit</button>
                  {isMythical && (
                    <button className="crm-dots-item crm-dots-item--enrich" onClick={() => { enrich(); setMenuOpen(false); }} disabled={enriching}>
                      {enriching ? 'Enriching…' : '✦ Enrich'}
                    </button>
                  )}
                  {isAdmin && (
                    <button className="crm-dots-item crm-dots-item--danger" onClick={() => { del(); setMenuOpen(false); }}>Delete</button>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="crm-detail-head">
            {!editing && (
              <div className="crm-detail-avatar">
                <span className="crm-detail-avatar-initials">{initials(contact.name)}</span>
                {photoSrc(contact) && (
                  <img className="crm-detail-avatar-img" src={photoSrc(contact)} alt=""
                    onError={e => { e.target.style.display = 'none'; }} />
                )}
              </div>
            )}
            {editing
              ? <input className="crm-detail-name-in" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Full name" />
              : (
                <div className="crm-detail-name-row">
                  <h2 className="crm-detail-name">{contact.name || 'Unnamed'}</h2>
                  {contact.linkedin_url && (
                    <a className="crm-li-btn" href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" title="View LinkedIn profile">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                  )}
                </div>
              )}
            {!editing && (contact.position || contact.company) && (
              <div className="crm-detail-meta">
                {contact.position && <p className="crm-detail-position">{contact.position}{isEnriched('position') && <ApolloMark />}</p>}
                {contact.company && (
                  <button className="crm-detail-co-btn" onClick={() => switchTab('company')}>
                    {contact.company}{isEnriched('company') && <ApolloMark />}
                  </button>
                )}
              </div>
            )}
            <div className="crm-detail-lists-row">
              {lists.length === 0
                ? <span className="crm-detail-lists-empty">Not in any marketing lists</span>
                : lists.map(l => (
                    <span key={l.id} className="crm-list-pill"
                      style={l.color ? { background: l.color + '22', color: l.color, borderColor: l.color + '55' } : {}}>
                      {l.name}
                    </span>
                  ))}
            </div>
          </div>

          {(editing || enrichMsg) && (
            <div className="crm-detail-acts">
              {editing && (
                <>
                  <button className="crm-detail-act crm-detail-act--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                  <button className="crm-detail-act" onClick={() => setEditing(false)}>Cancel</button>
                </>
              )}
              {enrichMsg && <span className="crm-enrich-msg">{enrichMsg}</span>}
            </div>
          )}

          {/* Contact info (moved here from the Overview tab) */}
          <div className="crm-detail-fields crm-ci-side">
            <p className="crm-detail-section-hd">Contact Info</p>
            <div className="crm-ci-list">
              {CONTACT_FIELDS.map(({ label, field, type, href, external, editOnly, icon, iconColor, iconBg }) => {
                if (editOnly && !editing) return null;
                const val = contact[field];
                return (
                  <div key={field} className="crm-ci-row">
                    <div className="crm-ci-icon-wrap" style={{ background: iconBg, color: iconColor }}>{icon}</div>
                    <div className="crm-ci-body">
                      <span className="crm-ci-lbl">{label}{isEnriched(field) && <ApolloMark />}</span>
                      {editing
                        ? <input className="crm-detail-field-in crm-ci-edit-in" type={type} value={form[field] || ''} onChange={e => set(field, e.target.value)} />
                        : val
                          ? href
                            ? <a className="crm-ci-val crm-ci-link" href={href(val)} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{val}</a>
                            : <span className="crm-ci-val">{val}</span>
                          : <span className="crm-ci-empty">Not on record</span>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="crm-detail-fields">
            {editing && (
              <>
                <div className="crm-detail-field">
                  <span className="crm-detail-field-lbl">Position</span>
                  <input className="crm-detail-field-in" value={form.position || ''} onChange={e => set('position', e.target.value)} />
                </div>
                <div className="crm-detail-field">
                  <span className="crm-detail-field-lbl">Company</span>
                  <input className="crm-detail-field-in" value={form.company || ''} onChange={e => set('company', e.target.value)} />
                </div>
              </>
            )}
            {/* Attorney connections — a person can be known by several attorneys,
                each with their own relationship type. */}
            <div className="crm-conn-field">
              <span className="crm-detail-field-lbl">Attorney Connections</span>
              <div className="crm-conn-list">
                {connections.length === 0 && <span className="crm-ci-empty">No attorney connections</span>}
                {connections.map(cn => (
                  <div key={cn.attorney_id} className="crm-conn-row">
                    <span className="crm-conn-atty">{cn.attorney_name || <em className="crm-em">Unknown</em>}</span>
                    {cn.relationship_type && (
                      <span className={`crm-conn-rel crm-rel-badge crm-rel-badge--${cn.relationship_type.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                        {cn.relationship_type}
                      </span>
                    )}
                    {isAdmin && (
                      <button className="crm-conn-x" title="Remove connection" onClick={() => removeConnection(cn.attorney_id)}>×</button>
                    )}
                  </div>
                ))}
              </div>
              {isAdmin && (addingConn ? (
                <div className="crm-conn-add">
                  <select className="crm-detail-field-in" value={connAtty} onChange={e => setConnAtty(e.target.value)}>
                    <option value="">Attorney…</option>
                    {attorneys
                      .filter(a => !connections.some(cn => String(cn.attorney_id) === String(a.id)))
                      .map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                  <select className="crm-detail-field-in" value={connRel} onChange={e => setConnRel(e.target.value)}>
                    <option value="">Relationship…</option>
                    {REL_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <div className="crm-conn-add-btns">
                    <button className="crm-detail-act crm-detail-act--primary" onClick={addConnection} disabled={!connAtty}>Add</button>
                    <button className="crm-detail-act" onClick={() => { setAddingConn(false); setConnAtty(''); setConnRel(''); }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button className="crm-conn-add-btn" onClick={() => setAddingConn(true)}>+ Add connection</button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Right panel ──────────────────────────────────────────────── */}
        <div className="crm-detail-right">
          <div className="crm-detail-tabbar">
            {[
              { key: 'overview',  label: 'Overview' },
              { key: 'activity',  label: 'Activity' },
              { key: 'emails',    label: 'Emails' },
              { key: 'company',   label: 'Company' },
              { key: 'notes',     label: 'Notes' },
            ].map(({ key, label }) => (
              <button key={key} className={`crm-detail-tab ${tab === key ? 'crm-detail-tab--on' : ''}`} onClick={() => switchTab(key)}>
                {label}
              </button>
            ))}
          </div>

          <div className="crm-detail-content">
            {/* ── Overview ── */}
            {tab === 'overview' && (
              <>
                {/* Employment history */}
                {!editing && employment && employment.length > 0 && (
                  <div className="crm-ov-block">
                    <p className="crm-detail-section-hd">Employment History <ApolloMark /></p>
                    <div className="crm-emp-list">
                      {employment.map(job => (
                        <div key={job.id} className={`crm-emp-item${job.is_current ? ' crm-emp-item--current' : ''}`}>
                          <div className="crm-emp-dot" />
                          <div className="crm-emp-body">
                            <span className="crm-emp-org">{job.organization_name || '—'}</span>
                            {job.title && <span className="crm-emp-title">{job.title}</span>}
                            <span className="crm-emp-dates">
                              {fmtEmpDate(job.start_date)}
                              {job.start_date || job.end_date ? ' – ' : ''}
                              {job.is_current ? 'Present' : fmtEmpDate(job.end_date)}
                            </span>
                          </div>
                          {job.is_current && <span className="crm-emp-badge">Current</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}


                {/* Notes */}
                {!editing && (
                  <div className="crm-ov-block">
                    <p className="crm-detail-section-hd">Notes</p>
                    {contact.notes ? (
                      <div className="crm-ov-notes">
                        <p className="crm-ov-notes-body">{contact.notes}</p>
                        <button className="crm-ov-notes-link" onClick={() => switchTab('notes')}>Edit notes →</button>
                      </div>
                    ) : (
                      <button className="crm-ov-notes-add" onClick={() => switchTab('notes')}>+ Add a note</button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ── Activity ── */}
            {tab === 'activity' && (
              <div className="crm-activity-wrap">
                <div className="crm-activity-header">
                  <p className="crm-detail-section-hd" style={{ margin: 0 }}>Activity Log</p>
                  {!addingAct && (
                    <button className="crm-activity-add-btn" onClick={() => setAddingAct(true)}>+ Log activity</button>
                  )}
                </div>

                {addingAct && (
                  <div className="crm-act-form">
                    <input
                      className="crm-act-form-pill"
                      placeholder="Activity type (e.g. Coffee, Call, Email)"
                      value={actForm.activity_type}
                      onChange={e => setActForm(f => ({ ...f, activity_type: e.target.value }))}
                    />
                    <input
                      className="crm-act-form-pill"
                      type="date"
                      value={actForm.activity_date}
                      onChange={e => setActForm(f => ({ ...f, activity_date: e.target.value }))}
                    />
                    {isAdmin && (
                      <select
                        className="crm-act-form-pill"
                        value={actForm.logged_by_id}
                        onChange={e => setActForm(f => ({ ...f, logged_by_id: e.target.value }))}
                      >
                        <option value="">Logged by (default: you)</option>
                        {attorneys.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    )}
                    <textarea
                      className="crm-act-form-ta"
                      placeholder="Notes (optional)"
                      value={actForm.note}
                      onChange={e => setActForm(f => ({ ...f, note: e.target.value }))}
                      rows={2}
                    />
                    <div className="crm-act-form-btns">
                      <button className="crm-act-form-save" onClick={submitActivity} disabled={!actForm.activity_type.trim() || savingAct}>
                        {savingAct ? 'Saving…' : 'Save'}
                      </button>
                      <button className="crm-act-form-cancel" onClick={() => { setAddingAct(false); setActForm({ activity_date: '', activity_type: '', note: '', logged_by_id: '' }); }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {activities === null ? (
                  <p className="crm-activity-loading">Loading…</p>
                ) : activities.length === 0 && !addingAct ? (
                  <div className="crm-tab-empty">
                    <div className="crm-tab-empty-icon">⚡</div>
                    <p className="crm-tab-empty-hd">No activity yet</p>
                    <p className="crm-tab-empty-sub">Use "Log activity" above to record interactions.</p>
                  </div>
                ) : (
                  <div className="crm-act-list">
                    {activities.map(act => (
                      <div key={act.id} className="crm-act-item" style={{ '--act-c': actTypeColor(act.activity_type) }}>
                        <div className="crm-act-body">
                          <div className="crm-act-row-main">
                            <span className="crm-act-type">{act.activity_type || '—'}</span>
                            <div className="crm-act-right">
                              <span className="crm-act-who-init">{initials(act.staff_name || act.attribution || '?')}</span>
                              <span className="crm-act-who">{act.staff_name || act.attribution || 'Unknown'}</span>
                              {isAdmin && (
                                <button className="crm-act-del" onClick={() => deleteActivity(act.id)} title="Delete">✕</button>
                              )}
                            </div>
                          </div>
                          {act.activity_date && (
                            <span className="crm-act-date">
                              {new Date(act.activity_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          )}
                          {act.note && <p className="crm-act-note">{act.note}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Emails ── */}
            {tab === 'emails' && (
              <div className="crm-tab-empty">
                <div className="crm-tab-empty-icon">✉</div>
                <p className="crm-tab-empty-hd">No emails sent yet</p>
                <p className="crm-tab-empty-sub">Emails sent to {contact.name || 'this contact'} via the CRM Hub will appear here.</p>
              </div>
            )}

            {/* ── Company ── */}
            {tab === 'company' && (
              <div className="crm-co-tab">
                {!contact.company ? (
                  <div className="crm-tab-empty">
                    <div className="crm-tab-empty-icon">🏢</div>
                    <p className="crm-tab-empty-hd">No company on record</p>
                    <p className="crm-tab-empty-sub">Add a company to this contact to see colleagues here.</p>
                  </div>
                ) : coContacts === null ? (
                  <p className="crm-em" style={{ padding: '40px', textAlign: 'center' }}>Loading…</p>
                ) : (
                  <CompanyView
                    companyName={contact.company}
                    contacts={coContacts}
                    compInfo={compInfo}
                    activeContactId={contact.id}
                    onOpenContact={null}
                  />
                )}
              </div>
            )}

            {/* ── Notes ── */}
            {tab === 'notes' && (
              <div className="crm-notes-panel">
                <p className="crm-detail-section-hd">Notes</p>
                <textarea
                  className="crm-notes-ta"
                  placeholder="Add notes about this contact…"
                  value={form.notes || ''}
                  onChange={e => set('notes', e.target.value)}
                  rows={14}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                  <button className="crm-btn crm-btn--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Notes'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Upload wizard ───────────────────────────────────────────────────────── */
const UploadModal = ({ currentUser, attorneys, onClose, onImported }) => {
  const [step, setStep]       = useState(1);
  const [file, setFile]       = useState(null);
  const [parseData, setParseData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [attorneyId, setAttorneyId] = useState(currentUser?.id !== 0 ? String(currentUser?.id || '') : '');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [result, setResult]   = useState(null);

  const pick = e => { if (e.target.files[0]) { setFile(e.target.files[0]); setError(''); } };

  const parse = async () => {
    setLoading(true); setError('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/contacts/parse`, fd, {
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
      });
      setParseData(res.data);
      setMapping(res.data.suggested || {});
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not parse file');
    } finally { setLoading(false); }
  };

  const importContacts = async () => {
    setLoading(true); setError('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mapping', JSON.stringify(mapping));
    if (attorneyId) fd.append('attorney_id', attorneyId);
    try {
      const res = await axios.post(`${API}/contacts/import`, fd, {
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      setStep(3);
      onImported();
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally { setLoading(false); }
  };

  const uploaderName = attorneys.find(a => a.id === parseInt(attorneyId))?.name || (currentUser?.id !== 0 ? currentUser?.name : null);

  return (
    <div className="crm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="crm-wizard">
        <div className="crm-wizard-head">
          <h3>Import Contacts</h3>
          <button className="crm-modal-x crm-modal-x--light" onClick={onClose}>✕</button>
        </div>

        {step === 1 && (
          <div className="crm-wizard-body">
            <p className="crm-hint">Upload a <strong>.xlsx</strong> or <strong>.csv</strong> file. Column names will be auto-detected.</p>

            <div className="crm-wizard-section-label">Importing as attorney</div>
            <select className="crm-wizard-select" value={attorneyId} onChange={e => setAttorneyId(e.target.value)}>
              <option value="">— Select attorney —</option>
              {attorneys.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            {uploaderName && <p className="crm-hint-small">Contacts will be assigned to <strong>{uploaderName}</strong></p>}

            <label className="crm-file-drop">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{color:'#aaa',marginBottom:8}}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17,8 12,3 7,8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              <span>{file ? file.name : 'Click to choose file'}</span>
              <span className="crm-file-sub">.xlsx or .csv</span>
              <input type="file" accept=".xlsx,.csv,.xls" onChange={pick} style={{ display:'none' }} />
            </label>

            {error && <p className="crm-error">{error}</p>}
            <div className="crm-wizard-footer">
              <button className="crm-btn" onClick={onClose}>Cancel</button>
              <button className="crm-btn crm-btn--primary" onClick={parse} disabled={!file || loading}>
                {loading ? 'Reading…' : 'Next →'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && parseData && (
          <div className="crm-wizard-body">
            <p className="crm-hint">Found <strong>{parseData.total}</strong> rows. Confirm column mapping below.</p>

            {uploaderName && (
              <div className="crm-import-as-row">
                <span>Importing as:</span>
                <strong>{uploaderName}</strong>
                {currentUser?.isAdmin && (
                  <select className="crm-import-as-select" value={attorneyId} onChange={e => setAttorneyId(e.target.value)}>
                    <option value="">— None —</option>
                    {attorneys.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                )}
              </div>
            )}

            <div className="crm-map-grid">
              {FIELDS.map(f => (
                <div key={f.key} className="crm-map-row">
                  <label className="crm-map-label">{f.label}</label>
                  <select
                    className="crm-map-select"
                    value={mapping[f.key] || ''}
                    onChange={e => setMapping(m => ({ ...m, [f.key]: e.target.value || undefined }))}
                  >
                    <option value="">— skip —</option>
                    {parseData.headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <div className="crm-preview-label">Preview (first 5 rows)</div>
            <div className="crm-preview-wrap">
              <table className="crm-preview-table">
                <thead><tr>{parseData.headers.map(h => <th key={h}>{h}</th>)}</tr></thead>
                <tbody>
                  {parseData.preview.map((row, i) => (
                    <tr key={i}>{parseData.headers.map(h => <td key={h}>{String(row[h] ?? '')}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>

            {error && <p className="crm-error">{error}</p>}
            <div className="crm-wizard-footer">
              <button className="crm-btn" onClick={() => setStep(1)}>← Back</button>
              <button className="crm-btn crm-btn--primary" onClick={importContacts} disabled={loading}>
                {loading ? 'Importing…' : `Import ${parseData.total} contacts`}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="crm-wizard-body crm-success-body">
            <div className="crm-success-icon">✓</div>
            <h4>Import complete</h4>
            <p><strong>{result?.imported}</strong> contacts added.</p>
            <button className="crm-btn crm-btn--primary" onClick={onClose}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Tag manager ─────────────────────────────────────────────────────────── */
const TagManager = ({ tags, onClose, onChanged }) => {
  const [name, setName]   = useState('');
  const [color, setColor] = useState('#272e40');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/tags`, { name: name.trim(), color }, auth());
      setName(''); setColor('#272e40');
      onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete tag? It will be removed from all contacts.')) return;
    await axios.delete(`${API}/tags/${id}`, auth()).catch(() => {});
    onChanged();
  };

  return (
    <div className="crm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="crm-wizard crm-wizard--narrow">
        <div className="crm-wizard-head">
          <h3>Manage Tags</h3>
          <button className="crm-modal-x crm-modal-x--light" onClick={onClose}>✕</button>
        </div>
        <div className="crm-wizard-body">
          <div className="crm-tag-create-row">
            <input className="crm-modal-input" placeholder="New tag name…" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && create()} style={{flex:1}} />
            <input type="color" className="crm-color-input" value={color} onChange={e => setColor(e.target.value)} />
            <button className="crm-btn crm-btn--primary" onClick={create} disabled={saving || !name.trim()}>Add</button>
          </div>
          {error && <p className="crm-error">{error}</p>}
          <div className="crm-tag-list">
            {tags.map(t => (
              <div key={t.id} className="crm-tag-list-row">
                <span className="crm-tag">{t.name}</span>
                <span className="crm-tag-count">{t.contact_count} contacts</span>
                <button className="crm-tag-del" onClick={() => del(t.id)}>×</button>
              </div>
            ))}
            {tags.length === 0 && <p className="crm-em" style={{padding:'8px 0'}}>No tags yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Mass email ──────────────────────────────────────────────────────────── */
const EmailModal = ({ tags, selectedIds, onClose }) => {
  const [subject, setSubject]   = useState('');
  const [body, setBody]         = useState('');
  const [selTags, setSelTags]   = useState([]);
  const [useSelected, setUseSel] = useState(selectedIds.length > 0);
  const [sending, setSending]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState('');

  const toggleTag = id => setSelTags(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { setError('Subject and body required'); return; }
    const payload = { subject, body };
    if (useSelected && selectedIds.length > 0) payload.contact_ids = selectedIds;
    else payload.tag_ids = selTags;
    setSending(true); setError('');
    try {
      const res = await axios.post(`${API}/email/send`, payload, auth());
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <div className="crm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="crm-wizard crm-wizard--wide">
        <div className="crm-wizard-head">
          <h3>Send Mass Email</h3>
          <button className="crm-modal-x crm-modal-x--light" onClick={onClose}>✕</button>
        </div>
        <div className="crm-wizard-body">
          {result ? (
            <div className="crm-success-body">
              <div className="crm-success-icon">✓</div>
              <h4>Sent</h4>
              <p>Delivered to <strong>{result.sent}</strong> recipients.</p>
              <button className="crm-btn crm-btn--primary" onClick={onClose}>Done</button>
            </div>
          ) : (
            <>
              <div className="crm-email-recipients">
                {selectedIds.length > 0 && (
                  <label className="crm-radio-row">
                    <input type="radio" checked={useSelected} onChange={() => setUseSel(true)} />
                    Send to {selectedIds.length} selected contacts
                  </label>
                )}
                <label className="crm-radio-row">
                  <input type="radio" checked={!useSelected} onChange={() => setUseSel(false)} />
                  Send by tag
                </label>
                {!useSelected && (
                  <div className="crm-tag-pick">
                    {tags.map(t => (
                      <label key={t.id} className="crm-tag-check">
                        <input type="checkbox" checked={selTags.includes(t.id)} onChange={() => toggleTag(t.id)} />
                        <span className="crm-tag">{t.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <input className="crm-modal-input" placeholder="Subject" value={subject} onChange={e => setSubject(e.target.value)} style={{marginBottom:8}} />
              <textarea className="crm-modal-textarea" placeholder="Email body (HTML supported)" value={body} onChange={e => setBody(e.target.value)} rows={8} />
              {error && <p className="crm-error">{error}</p>}
              <div className="crm-wizard-footer">
                <button className="crm-btn" onClick={onClose}>Cancel</button>
                <button className="crm-btn crm-btn--primary" onClick={send} disabled={sending}>
                  {sending ? 'Sending…' : 'Send Email'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Add contact modal ───────────────────────────────────────────────────── */
const AddContactModal = ({ attorneys, currentUser, companies, crm = 'firm', onClose, onAdded }) => {
  const empty = { name:'', work_email:'', personal_email:'', company:'', position:'',
                  work_phone:'', personal_phone:'', work_address:'', personal_address:'',
                  attorney_id: currentUser?.id !== 0 ? String(currentUser?.id || '') : '',
                  relationship_type:'', notes:'' };
  const [form, setForm]         = useState(empty);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [compSuggestions, setCompSuggestions] = useState([]);
  const [compOpen, setCompOpen] = useState(false);
  const [compInfo, setCompInfo] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCompany = (val) => {
    set('company', val);
    setCompInfo(null);
    if (val.trim().length > 0) {
      const q = val.toLowerCase();
      const matches = (companies || []).filter(c => c.toLowerCase().includes(q)).slice(0, 8);
      setCompSuggestions(matches);
      setCompOpen(matches.length > 0);
    } else {
      setCompSuggestions(companies?.slice(0, 8) || []);
      setCompOpen(true);
    }
  };

  const pickCompany = (name) => {
    set('company', name);
    setCompOpen(false);
    fetchCompanyInfo(name).then(info => { if (info) setCompInfo(info); });
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/contacts`, { ...form, crm }, auth());
      onAdded(); onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  };

  const otherFields = [
    { k:'name', l:'Full Name' }, { k:'work_email', l:'Work Email', t:'email' },
    { k:'personal_email', l:'Personal Email', t:'email' },
    { k:'position', l:'Position' }, { k:'work_phone', l:'Work Phone', t:'tel' },
    { k:'personal_phone', l:'Personal Phone', t:'tel' }, { k:'work_address', l:'Work Address' },
    { k:'personal_address', l:'Personal Address' },
  ];

  return (
    <div className="crm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="crm-wizard">
        <div className="crm-wizard-head">
          <h3>Add Contact</h3>
          <button className="crm-modal-x crm-modal-x--light" onClick={onClose}>✕</button>
        </div>
        <div className="crm-wizard-body">
          <div className="crm-map-grid">
            {otherFields.map(({ k, l, t }) => (
              <div key={k} className="crm-map-row">
                <label className="crm-map-label">{l}</label>
                <input className="crm-modal-input" type={t||'text'} value={form[k]} onChange={e => set(k, e.target.value)} />
              </div>
            ))}

            {/* Company typeahead */}
            <div className="crm-map-row" style={{ position: 'relative' }}>
              <label className="crm-map-label">Company</label>
              <input
                className="crm-modal-input"
                type="text"
                autoComplete="off"
                value={form.company}
                placeholder="Type to search or add new…"
                onChange={e => handleCompany(e.target.value)}
                onFocus={() => {
                  const q = form.company.toLowerCase();
                  const matches = q
                    ? (companies || []).filter(c => c.toLowerCase().includes(q)).slice(0, 8)
                    : (companies || []).slice(0, 8);
                  setCompSuggestions(matches);
                  setCompOpen(matches.length > 0);
                }}
                onBlur={() => setTimeout(() => setCompOpen(false), 150)}
              />
              {compOpen && (
                <div className="crm-co-suggest">
                  {compSuggestions.map(c => (
                    <button key={c} className="crm-co-suggest-item" onMouseDown={() => pickCompany(c)}>
                      {c}
                    </button>
                  ))}
                </div>
              )}
              {compInfo && !compOpen && (
                <div className="crm-co-card crm-co-card--inline">
                  {compInfo.domain && (
                    <CompanyLogo domain={compInfo.domain} className="crm-co-card-logo" />
                  )}
                  <div className="crm-co-card-body">
                    <span className="crm-co-card-name">{compInfo.name}</span>
                    {compInfo.domain && (
                      <a className="crm-co-card-domain" href={`https://${compInfo.domain}`} target="_blank" rel="noopener noreferrer">
                        {compInfo.domain} ↗
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="crm-map-row">
              <label className="crm-map-label">Assigned Attorney</label>
              <select className="crm-map-select" value={form.attorney_id} onChange={e => set('attorney_id', e.target.value)}>
                <option value="">— None —</option>
                {attorneys.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="crm-map-row">
              <label className="crm-map-label">Relationship</label>
              <select className="crm-map-select" value={form.relationship_type || ''} onChange={e => set('relationship_type', e.target.value)}>
                <option value="">— None —</option>
                <option>Client</option>
                <option>Prospect</option>
                <option>Referral Source</option>
                <option>Co-Counsel</option>
                <option>Opposing Counsel</option>
                <option>Expert Witness</option>
                <option>Judge / Court</option>
                <option>Government</option>
                <option>Friend / Personal</option>
                <option>Other</option>
              </select>
            </div>
            <div className="crm-map-row" style={{gridColumn:'1 / -1'}}>
              <label className="crm-map-label">Notes</label>
              <textarea className="crm-modal-textarea" value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} />
            </div>
          </div>
          {error && <p className="crm-error">{error}</p>}
          <div className="crm-wizard-footer">
            <button className="crm-btn" onClick={onClose}>Cancel</button>
            <button className="crm-btn crm-btn--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Add Contact'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Main CRM page ───────────────────────────────────────────────────────── */
const CRM = ({ currentUser }) => {
  const isAdmin = currentUser?.isAdmin;

  const [contacts, setContacts]   = useState([]);
  const [tags, setTags]           = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [loading, setLoading]     = useState(true);

  const [search, setSearch]             = useState('');
  const [filterAttorney, setFilterAtty] = useState('');
  const [filterTags, setFilterTags]     = useState([]);

  const [selected, setSelected]     = useState(new Set());
  const [openContact, setOpenContact] = useState(null);
  const [showCompany, setShowCompany] = useState(null);

  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const [showUpload, setShowUpload] = useState(false);
  const [showTags,   setShowTags]   = useState(false);
  const [showEmail,  setShowEmail]  = useState(false);
  const [showAdd,    setShowAdd]    = useState(false);

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const companies = useMemo(() =>
    [...new Set(contacts.map(c => c.company).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
  [contacts]);

  const displayContacts = useMemo(() => {
    if (!sortCol) return contacts;
    return [...contacts].sort((a, b) => {
      const va = (a[sortCol] || '').toLowerCase();
      const vb = (b[sortCol] || '').toLowerCase();
      const cmp = va.localeCompare(vb);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [contacts, sortCol, sortDir]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterAttorney) params.attorney_id = filterAttorney;
      if (filterTags.length) params.tag_ids = filterTags.join(',');
      const res = await axios.get(`${API}/contacts`, { ...auth(), params });
      setContacts(res.data);
    } catch { setContacts([]); }
    setLoading(false);
  }, [search, filterAttorney, filterTags]);

  const fetchTags      = useCallback(async () => { try { const r = await axios.get(`${API}/tags`, auth()); setTags(r.data); } catch {} }, []);
  const fetchAttorneys = useCallback(async () => { try { const r = await axios.get(`${API}/staff`, auth()); setAttorneys(r.data); } catch {} }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);
  useEffect(() => { fetchTags(); fetchAttorneys(); }, [fetchTags, fetchAttorneys]);

  const updateContact = useCallback(updated => {
    setContacts(cs => cs.map(c => c.id === updated.id ? { ...c, ...updated } : c));
    setOpenContact(prev => prev?.id === updated.id ? { ...prev, ...updated } : prev);
  }, []);

  const deleteContact = useCallback(id => {
    setContacts(cs => cs.filter(c => c.id !== id));
    setSelected(s => { const n = new Set(s); n.delete(id); return n; });
  }, []);

  const toggleSelect = id => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const toggleAll = () => selected.size === contacts.length
    ? setSelected(new Set())
    : setSelected(new Set(contacts.map(c => c.id)));

  const toggleFilterTag = id => setFilterTags(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const massDelete = async () => {
    const ids = [...selected];
    if (!window.confirm(`Permanently delete ${ids.length} contact${ids.length !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/contacts/bulk`, { ...auth(), data: { ids } });
      setContacts(cs => cs.filter(c => !selected.has(c.id)));
      setSelected(new Set());
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div className="crm-page">

      {/* Header */}
      <div className="crm-page-header">
        <p className="crm-page-sub">{contacts.length} contacts</p>
        <div className="crm-actions">
          <button className="crm-btn crm-btn--outline" onClick={() => setShowAdd(true)}>+ Add Contact</button>
          <button className="crm-btn crm-btn--outline" onClick={() => setShowUpload(true)}>↑ Import</button>
          {isAdmin && <button className="crm-btn crm-btn--outline" onClick={() => setShowTags(true)}>Tags</button>}
          {isAdmin && (
            <button className="crm-btn crm-btn--primary" onClick={() => setShowEmail(true)} disabled={selected.size === 0 && filterTags.length === 0}>
              Send Email
            </button>
          )}
          {isAdmin && selected.size > 0 && (
            <button className="crm-btn crm-btn--danger" onClick={massDelete}>
              Delete {selected.size}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="crm-filters">
        <div className="crm-search-wrap">
          <svg className="crm-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="22" y2="22"/>
          </svg>
          <input className="crm-search" placeholder="Search name, email, company…" value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button className="crm-search-clear" onClick={() => setSearch('')}>✕</button>}
        </div>

        <select className="crm-filter-select" value={filterAttorney} onChange={e => setFilterAtty(e.target.value)}>
          <option value="">All Attorneys</option>
          {attorneys.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>

        {tags.length > 0 && (
          <div className="crm-filter-tags">
            {tags.map(t => (
              <button
                key={t.id}
                className={`crm-filter-tag ${filterTags.includes(t.id) ? 'active' : ''}`}
                style={filterTags.includes(t.id) ? { background: t.color + '22', borderColor: t.color, color: t.color } : {}}
                onClick={() => toggleFilterTag(t.id)}
              >{t.name}</button>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead>
            <tr>
              <th className="crm-th-check">
                <input type="checkbox" checked={selected.size > 0 && selected.size === contacts.length} onChange={toggleAll} />
              </th>
              <SortTh col="name"          label="Name"       sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <SortTh col="company"       label="Company"    sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <SortTh col="position"      label="Position"   sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <SortTh col="work_email"    label="Work Email" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <SortTh col="attorney_name" label="Attorney"   sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <th>Tags</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="crm-table-empty">Loading…</td></tr>}
            {!loading && displayContacts.length === 0 && <tr><td colSpan={7} className="crm-table-empty">No contacts found.</td></tr>}
            {!loading && displayContacts.map(c => (
              <tr key={c.id} className={`crm-row ${selected.has(c.id) ? 'crm-row--sel' : ''}`} onClick={() => setOpenContact(c)}>
                <td className="crm-td-check" onClick={e => { e.stopPropagation(); toggleSelect(c.id); }}>
                  <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} onClick={e => e.stopPropagation()} />
                </td>
                <td className="crm-td-name">
                  <span className="crm-row-name">{c.name}</span>
                </td>
                <td>
                  {c.company
                    ? <button className="crm-company-link" onClick={e => { e.stopPropagation(); setShowCompany(c.company); }}>{c.company}</button>
                    : null}
                </td>
                <td className="crm-td-muted">{c.position}</td>
                <td className="crm-td-muted">{c.work_email}</td>
                <td className="crm-td-muted">{c.attorney_name}</td>
                <td>
                  <div className="crm-row-tags">
                    {(c.tags || []).slice(0, 2).map(t => <span key={t.id} className="crm-tag">{t.name}</span>)}
                    {(c.tags || []).length > 2 && <span className="crm-tag-more">+{c.tags.length - 2}</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {openContact && (
        <ContactModal
          contact={openContact}
          isAdmin={isAdmin}
          isMythical={currentUser?.isMythical}
          onClose={() => setOpenContact(null)}
          onUpdate={updateContact}
          onDelete={deleteContact}
        />
      )}
      {showUpload && (
        <UploadModal
          currentUser={currentUser}
          attorneys={attorneys}
          onClose={() => setShowUpload(false)}
          onImported={() => { fetchContacts(); }}
        />
      )}
      {showTags && (
        <TagManager tags={tags} onClose={() => setShowTags(false)} onChanged={fetchTags} />
      )}
      {showEmail && (
        <EmailModal tags={tags} selectedIds={[...selected]} onClose={() => setShowEmail(false)} />
      )}
      {showAdd && (
        <AddContactModal
          attorneys={attorneys}
          currentUser={currentUser}
          companies={companies}
          onClose={() => setShowAdd(false)}
          onAdded={fetchContacts}
        />
      )}
      {showCompany && (
        <CompanyModal
          companyName={showCompany}
          onClose={() => setShowCompany(null)}
          onOpenContact={c => setOpenContact(c)}
        />
      )}
    </div>
  );
};

export { ContactModal, AddContactModal, CompanyLogo, fetchCompanyInfo, photoSrc };
export default CRM;
